import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FiHelpCircle } from "react-icons/fi";
import { openHelpSidebar } from "../../redux/helpSlice";
import { manualSections } from "../manual/manualData";

export interface ContextualHelpButtonProps {
  sectionId?: string;
  variant?: "button" | "icon" | "badge" | "navbar";
  label?: string;
  title?: string;
  className?: string;
}

export default function ContextualHelpButton({
  sectionId,
  variant = "button",
  label,
  title,
  className = "",
}: ContextualHelpButtonProps) {
  const dispatch = useDispatch();
  const location = useLocation();

  // Resolve matching manual section
  const matchedSection = sectionId
    ? manualSections.find((s) => s.id === sectionId)
    : manualSections.find((s) => s.path === location.pathname);

  const resolvedSectionId = matchedSection?.id || sectionId || "dashboard";
  const sectionTitle = matchedSection?.title || "User Manual";
  const tooltipText = title || `Help: ${sectionTitle} Guide`;
  const displayText = label || `${sectionTitle} Guide`;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(openHelpSidebar(resolvedSectionId));
  };

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={handleClick}
        title={tooltipText}
        aria-label={tooltipText}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-[#B08D55] hover:border-[#B08D55]/50 hover:bg-[#B08D55]/5 transition-all shadow-sm active:scale-95 cursor-pointer ${className}`}
      >
        <FiHelpCircle className="w-4 h-4" />
      </button>
    );
  }

  if (variant === "badge") {
    return (
      <button
        type="button"
        onClick={handleClick}
        title={tooltipText}
        aria-label={tooltipText}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#B08D55]/10 text-[#B08D55] hover:bg-[#B08D55]/20 border border-[#B08D55]/20 transition-all active:scale-95 cursor-pointer ${className}`}
      >
        <FiHelpCircle className="w-3.5 h-3.5" />
        <span>{label || "Help Guide"}</span>
      </button>
    );
  }

  if (variant === "navbar") {
    return (
      <button
        type="button"
        onClick={handleClick}
        title={tooltipText}
        aria-label={tooltipText}
        className={`px-2.5 md:px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 border border-gray-200 bg-white text-gray-700 hover:text-[#B08D55] hover:border-[#B08D55]/40 hover:bg-[#B08D55]/5 transition-all shadow-sm active:scale-95 shrink-0 cursor-pointer ${className}`}
      >
        <FiHelpCircle className="w-3.5 h-3.5 text-[#B08D55]" />
        <span className="hidden sm:inline">Help</span>
      </button>
    );
  }

  // Default "button" variant
  return (
    <button
      type="button"
      onClick={handleClick}
      title={tooltipText}
      aria-label={tooltipText}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:text-[#B08D55] hover:border-[#B08D55]/40 hover:bg-[#B08D55]/5 transition-all shadow-sm active:scale-95 cursor-pointer ${className}`}
    >
      <FiHelpCircle className="w-4 h-4 text-[#B08D55]" />
      <span>{displayText}</span>
    </button>
  );
}

